import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ScrollArea } from '@/components/ui/scroll-area';

import type { SitrepSectionType } from 'components/DisasterGPT/hooks/useSitrepsV2';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';
import {
  ReportContentSearch,
  sectionMatchesReportSearch,
} from 'components/Reports/ReportContentSearch';
import SitrepDocumentOutline from './SitrepDocumentOutline';
import SitrepDocumentSection from './SitrepDocumentSection';
import {
  aiEnabledSectionIdsUnder,
  nestSitrepSections,
} from './nestSitrepSections';
import { useDocumentViewMode } from './useDocumentViewMode';

// Document layout always renders the continuous document; Tabs vs Document is
// chosen in the report kebab (reports.layoutMode).

const getScrollViewport = (root: HTMLElement | null) =>
  (root?.querySelector(
    '[data-slot="scroll-area-viewport"]'
  ) as HTMLElement | null) ?? root;

type SitrepDocumentViewProps = {
  sitrep: GetSitrepsResponse;
  sections: SitrepSectionType[];
  canEdit: boolean;
  // Draft-authoring affordances (AI generate / settings / delete) on headings.
  showAuthoringActions?: boolean;
  // Per-section AI generation locks held by other users.
  sectionAILocks?: Map<string, unknown>;
  isGenerating?: boolean;
  onRefetch: () => void;
  onOpenSettings?: (section: SitrepSectionType) => void;
  onDeleteSection?: (section: SitrepSectionType) => void;
  onAIGenerate?: (section: SitrepSectionType) => void;
  onAIGenerateMany?: (sectionIds: string[]) => void;
  // Move a section to a 0-based position. When provided (and
  // supports drag-to-reorder from the outline.
  onReorderSection?: (section: SitrepSectionType, targetOrder: number) => void;
  /** Rendered at the end of the document body column (not under the outline). */
  footer?: React.ReactNode;
  enableLiveAnalyticsRefresh?: boolean;
};

/**
 * Continuous document view of a sitrep: all sections render as one scrollable
 * document with an outline rail for navigation. A Focus mode renders one
 * section at a time, preserving the previous view-by-section experience.
 *
 * Sections remain the unit of editing, saving, versioning, and AI generation —
 * this component only changes how they are laid out and navigated.
 */
const SitrepDocumentView: React.FC<SitrepDocumentViewProps> = ({
  sitrep,
  sections,
  canEdit,
  showAuthoringActions = false,
  sectionAILocks,
  isGenerating = false,
  onRefetch,
  onOpenSettings,
  onDeleteSection,
  onAIGenerate,
  onAIGenerateMany,
  onReorderSection,
  footer,
  enableLiveAnalyticsRefresh = false,
}) => {
  const { outlineCollapsed, setOutlineCollapsed } = useDocumentViewMode();
  const [mobileOutlineOpen, setMobileOutlineOpen] = useState(false);
  const [reportQuery, setReportQuery] = useState('');
  const viewMode = 'document' as const;
  const nestedSections = useMemo(
    () => nestSitrepSections(sections),
    [sections]
  );
  const orderedSections = useMemo(
    () => nestedSections.map(({ children: _c, ...section }) => section),
    [nestedSections]
  );
  const childrenByParentId = useMemo(() => {
    const map = new Map<string, SitrepSectionType[]>();
    for (const nested of nestedSections) {
      if (nested.children.length) map.set(nested.id, nested.children);
    }
    return map;
  }, [nestedSections]);

  // Active section: reading position in document mode, selection in focus mode.
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const resolvedActiveId = useMemo(() => {
    if (!sections.length) return null;
    if (activeSectionId && sections.some((s) => s.id === activeSectionId)) {
      return activeSectionId;
    }
    const firstInDocument = nestedSections[0];
    return firstInDocument?.id ?? null;
  }, [sections, nestedSections, activeSectionId]);

  const bodyRef = useRef<HTMLDivElement | null>(null);
  const layoutRef = useRef<HTMLDivElement | null>(null);
  const programmaticScrollRef = useRef(false);
  const programmaticScrollTimerRef = useRef<number>(0);
  const pointerInBodyRef = useRef(false);
  const lastPointerRef = useRef<{ x: number; y: number } | null>(null);
  const selectableOutlineIds = useMemo(() => {
    const ids = new Set<string>();
    for (const root of nestedSections) {
      ids.add(root.id);
      root.children.forEach((child) => ids.add(child.id));
    }
    return ids;
  }, [nestedSections]);

  const resolveOutlineSectionId = useCallback(
    (sectionId: string | null | undefined) => {
      if (!sectionId) return null;
      if (selectableOutlineIds.has(sectionId)) return sectionId;
      const parent = nestedSections.find((section) => section.id === sectionId);
      return parent?.children[0]?.id ?? sectionId;
    },
    [nestedSections, selectableOutlineIds]
  );

  const syncActiveFromPointer = useCallback(
    (clientX: number, clientY: number) => {
      if (programmaticScrollRef.current) return;
      const scrollRoot = getScrollViewport(bodyRef.current);
      if (!scrollRoot) return;
      const hit = document.elementFromPoint(clientX, clientY);
      if (!(hit instanceof Element) || !scrollRoot.contains(hit)) return;
      const sectionEl = hit.closest<HTMLElement>('[data-section-id]');
      const headingId = resolveOutlineSectionId(sectionEl?.dataset.sectionId);
      if (!headingId) return;
      setActiveSectionId((current) =>
        current === headingId ? current : headingId
      );
    },
    [resolveOutlineSectionId]
  );

  const syncActiveFromTarget = useCallback(
    (target: EventTarget | null) => {
      if (programmaticScrollRef.current) return;
      if (!(target instanceof Element)) return;
      const sectionEl = target.closest<HTMLElement>('[data-section-id]');
      const headingId = resolveOutlineSectionId(sectionEl?.dataset.sectionId);
      if (!headingId) return;
      setActiveSectionId((current) =>
        current === headingId ? current : headingId
      );
    },
    [resolveOutlineSectionId]
  );

  useEffect(() => {
    const body = bodyRef.current;
    const scrollRoot = getScrollViewport(body);
    if (!body || !scrollRoot) return undefined;

    const onPointerOver = (event: PointerEvent) => {
      pointerInBodyRef.current = true;
      lastPointerRef.current = { x: event.clientX, y: event.clientY };
      syncActiveFromTarget(event.target);
    };
    const onPointerLeave = () => {
      pointerInBodyRef.current = false;
    };
    const onScroll = () => {
      if (!pointerInBodyRef.current || !lastPointerRef.current) return;
      syncActiveFromPointer(lastPointerRef.current.x, lastPointerRef.current.y);
    };

    body.addEventListener('pointerover', onPointerOver);
    body.addEventListener('pointerleave', onPointerLeave);
    scrollRoot.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      body.removeEventListener('pointerover', onPointerOver);
      body.removeEventListener('pointerleave', onPointerLeave);
      scrollRoot.removeEventListener('scroll', onScroll);
    };
  }, [orderedSections, syncActiveFromPointer, syncActiveFromTarget]);

  const handleOutlineSelect = (section: SitrepSectionType) => {
    setActiveSectionId(section.id);
    if (viewMode !== 'document') return;

    const scrollRoot = getScrollViewport(bodyRef.current);
    const target = bodyRef.current?.querySelector(
      `[data-testid="reports-doc-section"][data-section-id="${section.id}"]`
    ) as HTMLElement | null;
    if (!scrollRoot || !target) return;

    programmaticScrollRef.current = true;
    window.clearTimeout(programmaticScrollTimerRef.current);
    const nextTop =
      scrollRoot.scrollTop +
      (target.getBoundingClientRect().top -
        scrollRoot.getBoundingClientRect().top);
    const release = () => {
      programmaticScrollRef.current = false;
      window.clearTimeout(programmaticScrollTimerRef.current);
      scrollRoot.removeEventListener('scrollend', release);
    };
    scrollRoot.addEventListener('scrollend', release);
    programmaticScrollTimerRef.current = window.setTimeout(release, 800);
    scrollRoot.scrollTo({ top: Math.max(0, nextTop), behavior: 'smooth' });
  };

  // ── Drag-and-drop reorder (outline only) ───────────────────────────
  const canReorder = !!onReorderSection && showAuthoringActions;

  const renderSection = (section: SitrepSectionType) => (
    <div key={section.id} className="reports-doc__section-wrapper">
      <SitrepDocumentSection
        section={section}
        sitrepId={sitrep.id}
        sitrep={sitrep}
        sitrepWorkspaceId={sitrep.incident_id}
        sitrepTemplateId={sitrep.sitrep_template_id}
        canEdit={canEdit}
        showAuthoringActions={showAuthoringActions}
        isLocked={!!sectionAILocks?.get(section.id)}
        isGenerating={isGenerating}
        childSections={childrenByParentId.get(section.id) ?? []}
        sectionAILocks={sectionAILocks}
        onUpdated={onRefetch}
        onOpenSettings={onOpenSettings}
        onDelete={onDeleteSection}
        onAIGenerate={onAIGenerate}
        onAIGenerateMany={onAIGenerateMany}
        categoryAiIds={aiEnabledSectionIdsUnder(sections, section.id)}
        enableLiveAnalyticsRefresh={enableLiveAnalyticsRefresh}
      />
    </div>
  );

  const visibleSections = useMemo(
    () =>
      orderedSections.filter((section) =>
        sectionMatchesReportSearch(
          section,
          reportQuery,
          childrenByParentId.get(section.id) ?? []
        )
      ),
    [childrenByParentId, orderedSections, reportQuery]
  );

  const renderDocumentBody = () =>
    visibleSections.map((section) => renderSection(section));

  if (!orderedSections.length && !footer) {
    return null;
  }

  return (
    <div
      className="reports-doc"
      data-testid="reports-doc-view"
      data-tour="reports-sections"
    >
      <div className="reports-doc__layout" ref={layoutRef}>
        <SitrepDocumentOutline
          sections={sections}
          nestedSections={nestedSections}
          activeSectionId={resolvedActiveId}
          onSelect={(section) => {
            handleOutlineSelect(section);
            setMobileOutlineOpen(false);
          }}
          onReorder={canReorder ? onReorderSection : undefined}
          collapsed={outlineCollapsed}
          onToggleCollapsed={() => setOutlineCollapsed(!outlineCollapsed)}
          lockedSectionIds={
            sectionAILocks ? new Set(sectionAILocks.keys()) : undefined
          }
          mobileOpen={mobileOutlineOpen}
          onMobileOpenChange={setMobileOutlineOpen}
        />

        <div className="reports-doc__body" ref={bodyRef}>
          <ScrollArea className="tw:h-full tw:max-h-[70vh]">
            {orderedSections.length > 0 ? (
              <ReportContentSearch
                value={reportQuery}
                onChange={setReportQuery}
              />
            ) : null}
            {visibleSections.length > 0 ? (
              renderDocumentBody()
            ) : reportQuery.trim() ? (
              <p className="tw:text-sm tw:text-muted-foreground">
                No sections match that search.
              </p>
            ) : null}
            {footer}
          </ScrollArea>
        </div>
      </div>
    </div>
  );
};

export default SitrepDocumentView;
